import React, { useEffect, useMemo, useRef, useState } from 'react';

type Props = {
  promptText: string;
  disabled?: boolean;
  onSubmit: (comment: string) => void;
  onClose: () => void;
};

// プロジェクトに popup / popover 用の汎用コンポーネントがないため一時的に自前実装している。
// 将来的に汎用 popup コンポーネントが導入されたら、そちらに置き換える前提。
const FeedbackCommentPopup = ({ promptText, disabled = false, onSubmit, onClose }: Props) => {
  const [comment, setComment] = useState('');
  const popupRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickAway = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [onClose]);

  const trimmedComment = comment.trim();
  const isSubmitDisabled = trimmedComment.length === 0 || disabled;
  const styles = useMemo(() => getStyles(), []);

  const handleSubmit = () => {
    if (isSubmitDisabled) return;
    onSubmit(trimmedComment);
  };

  return (
    <div ref={popupRef} style={styles.popup} role="dialog" aria-label="feedback-comment-popup">
      <div style={styles.promptText}>{promptText}</div>
      <textarea
        style={styles.textarea}
        value={comment}
        onChange={event => setComment(event.target.value)}
        placeholder="ご自由にお書きください"
        rows={3}
        disabled={disabled}
      />
      <button
        type="button"
        style={isSubmitDisabled ? styles.submitButtonDisabled : styles.submitButton}
        disabled={isSubmitDisabled}
        onClick={handleSubmit}
      >
        送信する
      </button>
    </div>
  );
};

const getStyles = () =>
  ({
    popup: {
      position: 'absolute',
      top: 'calc(100% + 4px)',
      left: 0,
      width: '460px',
      padding: '12px',
      background: '#ffffff',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
      zIndex: 10,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    promptText: {
      fontSize: '12px',
      color: '#333',
      lineHeight: 1.5
    },
    textarea: {
      width: '100%',
      padding: '8px',
      fontSize: '13px',
      border: '1px solid #ccc',
      borderRadius: '4px',
      resize: 'vertical',
      fontFamily: 'inherit',
      boxSizing: 'border-box'
    },
    submitButton: {
      padding: '6px 12px',
      fontSize: '13px',
      color: '#fff',
      background: '#1976d2',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      alignSelf: 'flex-end'
    },
    submitButtonDisabled: {
      padding: '6px 12px',
      fontSize: '13px',
      color: '#fff',
      background: '#bdbdbd',
      border: 'none',
      borderRadius: '4px',
      cursor: 'not-allowed',
      alignSelf: 'flex-end'
    }
  } as const);

export default FeedbackCommentPopup;
